import * as v from 'valibot';

import { vcTwoLetterCountryCode } from '../../src/utils/valibot/custom-types';
import { newSchema } from '../../src/utils/valibot/schema';

/**
 * Keep in sync with backend com.bmw.ispi.customer.data.frontend.config.model.Feature enum.
 */
export enum CountryConfigurationFeature {
  IDENTITY_TITLE = 'IDENTITY_TITLE',
  IDENTITY_SUFFIX = 'IDENTITY_SUFFIX',
  IDENTITY_MIDDLE_NAME = 'IDENTITY_MIDDLE_NAME',
  IDENTITY_TAX_NUMBER = 'IDENTITY_TAX_NUMBER',
  IDENTITY_NATIONAL_ID = 'IDENTITY_NATIONAL_ID',
  IDENTITY_NATIONAL_ID_DOCUMENT_TYPE = 'IDENTITY_NATIONAL_ID_DOCUMENT_TYPE',
  ADDRESS_PROVINCE = 'ADDRESS_PROVINCE',
  DEALER_CONSENT = 'DEALER_CONSENT',
  VIN_SEARCH = 'VIN_SEARCH',
  LICENSE_SEARCH = 'LICENSE_SEARCH',
  CUSTOMER_INSIGHTS = 'CUSTOMER_INSIGHTS',
  WORKSHOP_CUSTOMER_STATUS = 'WORKSHOP_CUSTOMER_STATUS',
  C2V_REMOVE_VEHICLES = 'C2V_REMOVE_VEHICLES',
  C2V_REMOVE_RELATIONS = 'C2V_REMOVE_RELATIONS',
  C2V_ADD_VEHICLES = 'C2V_ADD_VEHICLES',
  C2V_ADD_RELATIONS = 'C2V_ADD_RELATIONS',
  C2V_EDIT_LICENSE_PLATE = 'C2V_EDIT_LICENSE_PLATE',
}

export const CountryConfigurationSettingSchema = newSchema(
  'CountryConfigurationSettingSchema',
  v.object({
    feature: v.enum(CountryConfigurationFeature),
    resolvedValue: v.boolean(),
  }),
);

export const CountryConfigurationSchema = newSchema(
  'CountryConfigurationSchema',
  v.record(v.string(), v.array(CountryConfigurationSettingSchema)),
);

export const UserLegalEntitySchema = newSchema(
  'UserLegalEntitySchema',
  v.object({
    distributionPartnerNr: v.string(),
    name: v.string(),
  }),
);

export const UserLegalEntitiesForCountrySchema = newSchema(
  'UserLegalEntitiesForCountrySchema',
  v.object({
    countryCode: vcTwoLetterCountryCode,
    legalEntities: v.array(UserLegalEntitySchema),
  }),
);

export const UserLegalEntitiesResponseSchema = newSchema(
  'UserLegalEntitiesResponseSchema',
  v.array(UserLegalEntitiesForCountrySchema),
);

/**
 * User represents a single unique WEN user in the customer data frontend.
 * The data is populated by the backend using the WEN userinfo endpoint.
 */
export const UserSchema = newSchema(
  'UserSchema',
  v.object({
    subject: v.string(),
    firstName: v.string(),
    lastName: v.string(),
    email: v.string(),
    roles: v.array(v.string()),
    defaultCountryCode: v.optional(vcTwoLetterCountryCode),
    countryCode: vcTwoLetterCountryCode,
    distributionPartnerNumber: v.optional(v.string()),
    distributionPartnerName: v.optional(v.string()),
    activeBusinessPartnerId: v.optional(v.string()),
    businessPartnerIdIsWhitelisted: v.boolean(),
    realm: v.string(),
    availableAdminLegalEntities: v.optional(UserLegalEntitiesResponseSchema),
    countryConfiguration: CountryConfigurationSchema,
    allowedToWrite: v.optional(v.boolean()),
  }),
);

export type CountryConfigurationSetting = v.InferOutput<typeof CountryConfigurationSettingSchema>;
export type CountryConfiguration = v.InferOutput<typeof CountryConfigurationSchema>;
export type UserLegalEntity = v.InferOutput<typeof UserLegalEntitySchema>;
export type UserLegalEntitiesResponse = v.InferOutput<typeof UserLegalEntitiesResponseSchema>;
export type User = v.InferOutput<typeof UserSchema>;

/**
 * The types of error served from the backend that also have a specific user-facing message.
 */
export enum DisplayedUserEndpointErrorType {
  // This is used when a 403 is returned from the backend with no response body,
  // because they were blocked by @RolesAllowed.
  MISSING_ROLE = 'MISSING_ROLE',

  STRONG_AUTHENTICATION_REQUIRED = 'STRONG_AUTHENTICATION_REQUIRED',
}

export class UserEndpointError extends Error {
  override name = 'UserEndpointError';

  readonly displayedType: string | undefined;

  /** The message is used for logging and debugging purposes, consisting of the code and ID concatenated */
  declare message: string;

  constructor(
    public readonly errorType: string,
    public readonly eventId?: string,
  ) {
    super(eventId ? `${errorType} ${eventId}` : errorType);

    this.displayedType = Object.values(DisplayedUserEndpointErrorType).find((r) => r === this.errorType);
  }
}
